import {Injectable} from '@angular/core';
import {environment} from 'src/environments/environment';
import {CONSTANTS} from "../../constants";

const ID_TOKEN = 'idToken';
const ACCESS_TOKEN = 'accessToken';
const REFRESH_TOKEN = 'refreshToken';
const REFRESH_URL = 'https://cognito-idp.eu-west-1.amazonaws.com/';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor() { }

  async login(): Promise<void> {
    // Check if the SSO flow is ongoing
    const queryString = new URLSearchParams(window.location.search);
    if (queryString.has('returnUrl') && queryString.get('returnUrl') !== CONSTANTS.ROUTES.HOME) {
      localStorage.setItem('returnUrl', queryString.get('returnUrl'));
    }
    if (queryString.has('code')) {
      // Create a verification code challenge
      const codeVerifier = btoa(Math.random().toString(32)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      // Create body
      const body = new URLSearchParams(Object.entries({
        grant_type: 'authorization_code',
        code: queryString.get('code'),
        client_id: environment.aws.amplify.aws_user_pools_web_client_id,
        redirect_uri: environment.aws.oauth.redirectSignIn,
        code_verifier: codeVerifier
      })).toString();

      fetch(`https://${environment.aws.oauth.domain}/oauth2/token`, {
        body,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      }).then(authResponse => {
        if (authResponse) {

          authResponse.json().then(auth => {
            if (auth && auth.access_token && auth.id_token && auth.refresh_token) {
              Object.keys(auth).forEach(k => {
                localStorage.setItem(k.replace('_t', 'T'), auth[k]);
              });

              let returnUrl = '';
              if (!!localStorage.getItem('returnUrl')) {
                returnUrl = !!localStorage.getItem('returnUrl') ? '&returnUrl=' + localStorage.getItem('returnUrl') : '';
                localStorage.removeItem('returnUrl');
              }
              window.location.href = `${window.location.href}${returnUrl}`;

            }
          });
        }
      }).catch(err => console.error(err));
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem(ID_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    window.location.reload();
  }

  async isAuthenticated(): Promise<boolean> {
    return new Promise((resolve, _reject) => {
      const current = localStorage.getItem(ID_TOKEN) || null;
      if (!current) {
        resolve(false);
      }
      const payload: IJwtTokenPayload = this.getJWTPayload(current);

      // JWT is not valid
      if (!payload?.exp) resolve(false);

      // JWT exists but is expired
      if (payload?.exp <= Math.round(new Date().getTime() / 1000)) {
        // JWT is not refreshable
        if (!localStorage.getItem(REFRESH_TOKEN)) {
          resolve(false);
        }

        // Then refresh token and retry
        this.refreshToken()
          .then(idToken => {
            (!idToken) ? resolve(false) : resolve(true);
          })
          .catch(_err => {
            localStorage.clear();
            resolve(false)
          });
      } else if (payload?.exp > Math.round(new Date().getTime() / 1000)) {
        // JWT exists and is not expired
        resolve(true);
      }
    })
  }

  async getIdToken(): Promise<string | null> {
    return await this._getToken(ID_TOKEN);
  }

  async getAccessToken(): Promise<string | null> {
    return await this._getToken(ACCESS_TOKEN);
  }

  private async _getToken(name: string): Promise<string | null> {
    return new Promise<string>((resolve, _reject) => {
      const current = localStorage.getItem(name) || null;

      if (current) {
        const payload: IJwtTokenPayload = this.getJWTPayload(current);
        if (payload?.exp && payload.exp > Math.round(new Date().getTime() / 1000)) {
          // the token is not expired
          resolve(current);
        } else {
          // the current token is expired, refresh
          this.refreshToken().then(idToken => {
            resolve(idToken);
          });
        }
      } else {
        return null;
      }
    });
  }

  async refreshToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (!refreshToken) return null;

    try {
      const refreshResult = await fetch(`https://${environment.aws.oauth.domain}/oauth2/token`, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        mode: 'cors',
        cache: 'no-cache',
        method: 'POST',
        body: `grant_type=refresh_token&client_id=${environment.aws.amplify.aws_user_pools_web_client_id}&refresh_token=${refreshToken}`,
      });

      const results = await refreshResult.json();
      if (!results?.access_token || !results?.id_token) {
        this.logout();
        return null;
      }

      localStorage.setItem(ID_TOKEN, results.id_token);
      localStorage.setItem(ACCESS_TOKEN, results.access_token);

      return results.id_token;
    } catch (err) {
      console.error(err);
      this.logout();
      return null;
    }
  }

  getJWTPayload(token: string) {
    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    }
  }

}

interface IJwtTokenPayload {
  at_hash: string;
  aud: string;
  auth_time: number;
  'cognito:groups': string[];
  'cognito:username': string;
  email: string;
  email_verified: boolean;
  exp: number;
  iat: number;
  iss: string;
  missions: string[];
  sub: string;
  token_use: string;
}